import { useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/common/Breadcrumb';
import CSVUpload from '@/components/common/CSVUpload';
import { useToast } from '@/components/common/Toast';
import { ParsedCSVData } from '@/types/upload';
import { validateLotoCsv, validateLotoCsvWarnings } from '@/utils/lotoCsvValidation';

const LotoUpload = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleUploadSuccess = (_data: ParsedCSVData) => {
    showToast('アップロードを受け付けました。処理状況はアップロード一覧で確認してください');
    navigate('/upload-management/new');
  };

  return (
    <div>
      <Breadcrumb />
      <div className="card max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-admin-text mb-2">くじ設定CSVアップロード</h1>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h2 className="font-semibold text-admin-text mb-2">
            📋 CSVファイルのフォーマットと注意事項
          </h2>
          <ul className="text-sm text-admin-textLight space-y-1 list-disc list-inside">
            <li>
              ファイル形式：CSV形式（.csv）のみ / サイズ 10MB 以下 / 文字コード UTF-8（BOM
              可）または Shift_JIS
            </li>
            <li>
              Book / Product と同じ 3 行構成：1 行目＝カラム名 / 2 行目＝日本語説明 / 3
              行目以降＝データ
            </li>
            <li>
              <strong>ブロック形式</strong>：くじ情報行 1 行 + 確率情報行 N 行 = 1
              設定。1ファイルに複数の設定（適用期間の異なるもの）を記述できる
            </li>
            <li>
              くじ情報行は <strong>1〜6列目</strong>（loto_name 〜 loto_image）だけを埋め、
              確率情報行は <strong>7〜12列目</strong>（rate_name 〜 rate_description）だけを埋める。
              <strong>両方を1行に書くと取込に失敗します</strong>
            </li>
            <li>
              日付列（start_at / end_at）は <strong>JST</strong>・形式は YYYY-MM-DD。 end_at は
              <strong>当日を含む</strong>（2026-09-30 なら 9/30 まで有効）
            </li>
            <li>
              chance_rate は<strong>100万分率</strong>。1設定内の合計が
              <strong>ちょうど 1,000,000</strong>（パーセントではない）
            </li>
            <li>
              win_point は<strong>eコインと 1:1</strong>（1 = 1eコイン / 500 = 500eコイン）。 0
              はハズレ区分になる
            </li>
            <li>
              同じくじの<strong>適用期間は重複させられません</strong>
              。既存設定の途中から新しい設定を 始める場合は、既存設定の end_at
              を前日に更新する行と新設定の行を
              <strong>同じCSVに併記</strong>してください（1回のアップロードで完結します）
            </li>
            <li>
              loto_image / rate_image は<strong>パス指定のみ</strong>。画像ファイル自体は事前に開発
              チームへ入稿を依頼してください
            </li>
            <li>
              <strong>CSVに書かなかった既存設定は削除されません</strong>（upsert）。過去設定は履歴と
              して保持されます。くじを止める場合は end_at を過去日にして再アップロードしてください
            </li>
            <li>1 件でもエラーがあるとその CSV は 1 件も反映されません</li>
            <li>
              <strong>登録済み設定との期間重複はこの画面では判定できません</strong>
              （登録済みデータの参照が必要なため）。抵触した場合はアップロード後に
              アップロード一覧で FAILED として表示されます
            </li>
          </ul>
        </div>

        <CSVUpload
          uploadType="Loto"
          customValidate={validateLotoCsv}
          customWarn={validateLotoCsvWarnings}
          onUploadSuccess={handleUploadSuccess}
        />

        <div className="flex gap-4 mt-6">
          <button
            onClick={() => navigate('/upload-management/new')}
            className="px-6 py-2 border border-gray-300 text-admin-text rounded-lg hover:bg-gray-50 transition-colors"
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
};

export default LotoUpload;
